import React, { useState, useRef, useEffect } from 'react';
import { MeiliSearch } from 'meilisearch';
import Toast from './Toast';
import { db, auth, storage } from '../firebaseConfig';
import { doc, getDoc, setDoc, collection, getDocs, updateDoc, deleteField, Timestamp, query, where, limit, writeBatch, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useParams } from 'react-router-dom';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';
import ConfirmationModal from './modals/ConfirmationModalActions';

import { setPipelineRefresh } from '../redux/pipelineRefresh';
import { useAppSelector, useAppDispatch } from '../redux/store';


interface AdmissionStatus {
    id: string;
    admissionStatus: string;
}

interface LeadAdmissionSubStatus {
    id: string;
    subAdmissionStatus: string;
    admissionStatusId: string;
}

interface FileSubmissionStatus {
    id: string;
    fileSubmissionStatus: string;
}

interface University {
    id: string;
    name: string;
    uniCode: string;
}

interface ApplicationMovedStatus {
    id: string;
    applicationMovedStatus: string;
}

interface OfferLetterStatus {
    id: string;
    offerLetterStatus: string;
}

interface FeePaymentStatus {
    id: string;
    feePaymentStatus: string;
}

interface SuperAgent {
    id: string;
    superAgent: string;
}

interface Country {
    id: string;
    name: string;
}

interface UniversityApplication {
    universityId: string;
    universityName: string;
    priority: number;
    countryId: string;
    countryName: string;
    admissionStatus: string;
    admissionStatusId: string;
    subAdmissionStatusId?: string;
    uploadedAdmissionStatusDocument: string;
    agencyName: string;
    programName: string;
    courseLink: string;
    programRemarks: string;
    applicationMovedStatus: string;
    applicationMovedStatusId: string;
    dateOfApplicationSubmission: Timestamp | null;
    conditionalOfferStatus: string;
    conditionalOfferStatusId: string;
    dateOfConditionalOfferReceipt: Timestamp | null;
    uploadedConditionalOfferLetter: string;
    unconditionalOfferStatus: string;
    unconditionalOfferStatusId: string;
    dateOfUnconditionalOfferReceipt: Timestamp | null;
    uploadedUnconditionalOfferLetter: string;
    totalFeeAmount: string;
    firstYearFeeAmount: string;
    feePaymentStatus: string;
    feePaymentStatusId: string;
    superAgentId: string;
    finalRemarks: string;
}

interface AppliedUniversities {
    write: boolean;
}

const AppliedUniversities = ({ write }: AppliedUniversities) => {

    const { currentSubAgent } = useSubAgentAuth();

    // University search and selection states
    const [universities, setUniversities] = useState<University[]>([]);
    const [universitySearchTerm, setUniversitySearchTerm] = useState('');
    const [isUniversityDropdownOpen, setIsUniversityDropdownOpen] = useState(false);
    const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
    const [selectedUniversities, setSelectedUniversities] = useState<UniversityApplication[]>([]);
    const [collapsedUniversities, setCollapsedUniversities] = useState<Set<string>>(new Set(selectedUniversities.map(u => u.universityId)));
    const universityInputRef = useRef<HTMLInputElement>(null);
    const universityDropdownRef = useRef<HTMLDivElement>(null);
    const { leadId: urlLeadId } = useParams();

    const [leadId, setLeadId] = useState(urlLeadId || "");
    const [leadName, setLeadName] = useState("");
    const [leadPhone, setLeadPhone] = useState("");

    // Country search and selection states
    const [countries, setCountries] = useState<Country[]>([]);
    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [isLoadingCountry, setIsLoadingCountry] = useState(false);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState<{ [key: string]: boolean }>({});

    // Status states
    const [admissionStatuses, setAdmissionStatuses] = useState<AdmissionStatus[]>([]);
    const [leadAdmissionSubStatuses, setLeadAdmissionSubStatuses] = useState<LeadAdmissionSubStatus[]>([]);
    const [applicationMovedStatuses, setApplicationMovedStatuses] = useState<ApplicationMovedStatus[]>([]);
    const [offerLetterStatuses, setOfferLetterStatuses] = useState<OfferLetterStatus[]>([]);
    const [feePaymentStatuses, setFeePaymentStatuses] = useState<FeePaymentStatus[]>([]);
    const [superAgents, setSuperAgents] = useState<SuperAgent[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [companyId, setCompanyId] = useState('');

    // modal states
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAdditionalActions, setAdditionalActions] = useState(false);

    const pipelineRefresh = useAppSelector(state => state.pipelineRefresh.refresh);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (leadId && companyId) {
            const fetchLeadData = async () => {
                try {
                    const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
                    if (leadDoc.exists()) {
                        const data = leadDoc.data();
                        setLeadName(data.name);
                        setLeadPhone(data.number);
                    }
                } catch (error) {
                    console.error("Error fetching lead data:", error);
                }
            };
            fetchLeadData();
        }
    }, [leadId, companyId]);

    // Handle country search
    const handleCountrySearchChange = async (universityId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const upperValue = value.toUpperCase();
        setCountrySearchTerm(value);

        if (value.length < 3) {
            setCountries([]);
            return;
        }

        setIsLoadingCountry(true);
        try {
            const countriesRef = collection(db, 'countries');
            const q = query(
                countriesRef,
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const countriesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setCountries(countriesData);
        } catch (error) {
            console.error("Error searching countries:", error);
            setToastMessage('Error searching countries');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsLoadingCountry(false);
        }
    };

    // Handle country selection
    const handleCountrySelect = (universityId: string, country: Country) => {
        const updatedUniversities = selectedUniversities.map(uni => {
            if (uni.universityId === universityId) {
                return { ...uni, countryId: country.id, countryName: country.name };
            }
            return uni;
        });
        setSelectedUniversities(updatedUniversities);
        setIsCountryDropdownOpen({ ...isCountryDropdownOpen, [universityId]: false });
        setCountrySearchTerm(country.name);
        setCountries([]);
    };

    // Handle university search
    const handleUniversitySearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setUniversitySearchTerm(value);

        if (value.length < 3) {
            setUniversities([]);
            return;
        }

        setIsLoadingUniversities(true);
        try {
            // Initialize Meilisearch client using environment variables
            const client = new MeiliSearch({
                host: process.env.REACT_APP_MEILISEARCH_URL || 'https://search.scholarcred.com',
                apiKey: process.env.REACT_APP_MEILISEARCH_API_KEY || '80cdae41311553c99f1835f65ab59832b662cb1a7ac50deb4edd3f520b47bfc1',
            });

            // Search the universities index
            const searchResults = await client.index('universities').search(value, {
                limit: 10
            });

            // Format results to match the existing data structure
            const universitiesData = searchResults.hits.map((hit: any) => ({
                id: hit.id,
                name: hit.name,
                uniCode: hit.uniCode || '' // Preserve uniCode if present, empty string if not
            }));

            setUniversities(universitiesData);
        } catch (error) {
            console.error("Error searching universities:", error);
        } finally {
            setIsLoadingUniversities(false);
        }
    };

    // Handle university selection
    const handleUniversitySelect = (university: University) => {
        const isAlreadySelected = selectedUniversities.some(u => u.universityId === university.id);
        if (!isAlreadySelected) {
            const newUniversity: UniversityApplication = {
                universityId: university.id,
                universityName: university.name,
                priority: selectedUniversities.length + 1,
                countryId: '',
                countryName: '',
                admissionStatus: '',
                admissionStatusId: '',
                subAdmissionStatusId: '',
                uploadedAdmissionStatusDocument: '',
                agencyName: '',
                programName: '',
                courseLink: '',
                programRemarks: '',
                applicationMovedStatus: '',
                applicationMovedStatusId: '',
                dateOfApplicationSubmission: null,
                conditionalOfferStatus: '',
                conditionalOfferStatusId: '',
                dateOfConditionalOfferReceipt: null,
                uploadedConditionalOfferLetter: '',
                unconditionalOfferStatus: '',
                unconditionalOfferStatusId: '',
                dateOfUnconditionalOfferReceipt: null,
                uploadedUnconditionalOfferLetter: '',
                totalFeeAmount: '',
                firstYearFeeAmount: '',
                feePaymentStatus: '',
                feePaymentStatusId: '',
                superAgentId: '',
                finalRemarks: ''
            };
            setSelectedUniversities([...selectedUniversities, newUniversity]);
            // Remove the new university from collapsed set to show it expanded
            setCollapsedUniversities(prev => {
                const newSet = new Set(prev);
                newSet.delete(university.id);
                return newSet;
            });
        }
        setUniversitySearchTerm('');
        setUniversities([]);
        setIsUniversityDropdownOpen(false);
    };

    // Handle university removal
    const handleRemoveUniversity = (universityId: string) => {
        setSelectedUniversities(selectedUniversities.filter(u => u.universityId !== universityId));
        setCollapsedUniversities(prev => {
            const newSet = new Set(Array.from(prev));
            newSet.delete(universityId);
            return newSet;
        });
    };

    // Load statuses and data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) return;

                let companyId: string | undefined;
                // Get user document to get companyId
                if (currentSubAgent) {
                    companyId = currentSubAgent.companyId;
                } else {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    companyId = userDoc.data()?.companyId;
                }
                if (!companyId) return;

                setCompanyId(companyId);

                // Fetch admission statuses
                const admissionStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadAdmissionStatus`));
                const admissionStatusesData = admissionStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    admissionStatus: doc.data().admissionStatus
                }));
                setAdmissionStatuses(admissionStatusesData);

                // Fetch leadAdmissionSubStatus
                const leadAdmissionSubStatusSnapshot = await getDocs(collection(db, `companies/${companyId}/leadAdmissionSubStatus`));
                const leadAdmissionSubStatusData = leadAdmissionSubStatusSnapshot.docs.map(doc => ({
                    id: doc.id,
                    subAdmissionStatus: doc.data().subAdmissionStatus,
                    admissionStatusId: doc.data().admissionStatusId
                }));
                // console.log('Lead Admission Sub Statuses:', leadAdmissionSubStatusData);
                setLeadAdmissionSubStatuses(leadAdmissionSubStatusData);

                // Fetch application moved statuses
                const applicationMovedStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadApplicationMovedStatus`));
                const applicationMovedStatusesData = applicationMovedStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    applicationMovedStatus: doc.data().applicationMovedStatus
                }));
                setApplicationMovedStatuses(applicationMovedStatusesData);

                // Fetch offer letter statuses
                const offerLetterStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadOfferLetterStatus`));
                const offerLetterStatusesData = offerLetterStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    offerLetterStatus: doc.data().offerLetterStatus
                }));
                setOfferLetterStatuses(offerLetterStatusesData);

                // Set up real-time listeners for university names
                const unsubscribeCallbacks: (() => void)[] = [];

                // Fetch fee payment statuses
                const feePaymentStatusesSnapshot = await getDocs(collection(db, `companies/${companyId}/leadFeePaymentStatus`));
                const feePaymentStatusesData = feePaymentStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    feePaymentStatus: doc.data().feePaymentStatus
                }));
                setFeePaymentStatuses(feePaymentStatusesData);

                // Fetch super agents 
                const superAgentsSnapshot = await getDocs(collection(db, `companies/${companyId}/superAgents`));
                const superAgentsData = superAgentsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    superAgent: doc.data().superAgent
                }));
                setSuperAgents(superAgentsData);

                // Fetch applied universities
                if (leadId) {
                    const universitiesCollection = collection(db, `companies/${companyId}/leads/${leadId}/appliedUniversities`);
                    const universitiesSnapshot = await getDocs(universitiesCollection);
                    const universitiesData = await Promise.all(universitiesSnapshot.docs.map(async (univDoc) => {
                        const universityRef = doc(db, 'universities', univDoc.id);
                        const universityDoc = await getDoc(universityRef);
                        const universityData = universityDoc.data();
                        const univData = univDoc.data();
                        return {
                            universityId: univDoc.id,
                            universityName: universityData && typeof universityData === 'object' && 'name' in universityData ? universityData.name : 'Unknown University',
                            priority: univData.priority || 0,
                            countryId: univData.countryId || '',
                            countryName: univData.countryName || '',
                            applicationMovedStatus: '',
                            applicationMovedStatusId: univData.applicationMovedStatusId || '',
                            dateOfApplicationSubmission: univData.dateOfApplicationSubmission || '',
                            conditionalOfferStatus: '',
                            conditionalOfferStatusId: univData.conditionalOfferStatusId || '',
                            dateOfConditionalOfferReceipt: univData.dateOfConditionalOfferReceipt || '',
                            uploadedConditionalOfferLetter: univData.uploadedConditionalOfferLetter || '',
                            unconditionalOfferStatus: '',
                            unconditionalOfferStatusId: univData.unconditionalOfferStatusId || '',
                            dateOfUnconditionalOfferReceipt: univData.dateOfUnconditionalOfferReceipt || '',
                            uploadedUnconditionalOfferLetter: univData.uploadedUnconditionalOfferLetter || '',
                            totalFeeAmount: univData.totalFeeAmount || '',
                            firstYearFeeAmount: univData.firstYearFeeAmount || '',
                            feePaymentStatus: '',
                            feePaymentStatusId: univData.feePaymentStatusId || '',
                            finalRemarks: univData.finalRemarks || '',
                            admissionStatus: univData.admissionStatus || '',
                            admissionStatusId: univData.admissionStatusId || '',
                            subAdmissionStatusId: univData.subAdmissionStatusId || null,
                            uploadedAdmissionStatusDocument: univData.uploadedAdmissionStatusDocument || '',
                            agencyName: univData.agencyName || '',
                            programName: univData.programName || '',
                            courseLink: univData.courseLink || '',
                            programRemarks: univData.programRemarks || '',
                            superAgentId: univData.superAgents || null,
                        };
                    }));
                    setSelectedUniversities(universitiesData);
                    // Only collapse universities that were previously saved
                    const savedUniversityIds = universitiesSnapshot.docs.map(doc => doc.id);
                    setCollapsedUniversities(new Set(savedUniversityIds));

                    // Fetch university names
                    const updatedUniversitiesData = await Promise.all(universitiesData.map(async (university) => {
                        const universityRef = doc(db, 'universities', university.universityId);
                        const universityDoc = await getDoc(universityRef);
                        if (universityDoc.exists()) {
                            const universityData = universityDoc.data();
                            return { ...university, universityName: universityData.name };
                        }
                        return university;
                    }));
                    // Sort by priority and assign priorities if not present
                    const sortedUniversities = updatedUniversitiesData.sort((a, b) => (a.priority || Infinity) - (b.priority || Infinity));
                    const universitiesWithPriority = sortedUniversities.map((uni, index) => ({
                        ...uni,
                        priority: uni.priority || index + 1
                    }));
                    setSelectedUniversities(universitiesWithPriority);

                }

                // No cleanup needed since we're not using real-time listeners
                return () => { };
            } catch (error) {
                console.error('Error fetching data:', error);
                setToastMessage('Error fetching data');
                setIsErrorToast(true);
                setShowToast(true);
            }
        };

        fetchData();
    }, [leadId]);

    // Handle click outside university dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                universityDropdownRef.current &&
                universityInputRef.current &&
                !universityDropdownRef.current.contains(event.target as Node) &&
                !universityInputRef.current.contains(event.target as Node)
            ) {
                setIsUniversityDropdownOpen(false);
                setUniversitySearchTerm('');
                setUniversities([]);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle saving university application data
    const handleMoveUniversity = (universityId: string, direction: 'up' | 'down') => {
        const currentIndex = selectedUniversities.findIndex(u => u.universityId === universityId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= selectedUniversities.length) return;

        const updatedUniversities = [...selectedUniversities];
        const temp = updatedUniversities[currentIndex];
        updatedUniversities[currentIndex] = updatedUniversities[newIndex];
        updatedUniversities[newIndex] = temp;

        // Update priorities
        const reorderedUniversities = updatedUniversities.map((uni, index) => ({
            ...uni,
            priority: index + 1
        }));

        setSelectedUniversities(reorderedUniversities);
    };

    const confirmSave = () => {
        // Open confirmation modal
        setIsConfirmModalOpen(true);
    }

    const handleSave = async () => {
        try {
            if (!leadId || !companyId) return;

            // Helper function to sanitize data (convert empty strings to null)
            const sanitizeData = (data: any) => {
                const sanitized: Record<string, any> = {};
                Object.keys(data).forEach(key => {
                    if (data[key] === undefined) {
                        // Skip undefined values
                        return;
                    }
                    sanitized[key] = (typeof data[key] === 'string' && data[key].trim() === '')
                        ? null
                        : data[key];
                });
                return sanitized;
            };

            // Save each university as a separate document
            const batch = writeBatch(db);
            selectedUniversities.forEach(university => {
                // Sanitize the university data
                const sanitizedUniversity = sanitizeData({
                    priority: university.priority,
                    countryId: university.countryId,
                    countryName: university.countryName,
                    admissionStatus: university.admissionStatus,
                    admissionStatusId: university.admissionStatusId,
                    subAdmissionStatusId: university.subAdmissionStatusId || null,
                    uploadedAdmissionStatusDocument: university.uploadedAdmissionStatusDocument,
                    agencyName: university.agencyName,
                    programName: university.programName,
                    courseLink: university.courseLink,
                    programRemarks: university.programRemarks,
                    applicationMovedStatus: university.applicationMovedStatus,
                    applicationMovedStatusId: university.applicationMovedStatusId,
                    dateOfApplicationSubmission: university.dateOfApplicationSubmission,
                    conditionalOfferStatus: university.conditionalOfferStatus,
                    conditionalOfferStatusId: university.conditionalOfferStatusId,
                    dateOfConditionalOfferReceipt: university.dateOfConditionalOfferReceipt,
                    uploadedConditionalOfferLetter: university.uploadedConditionalOfferLetter,
                    unconditionalOfferStatus: university.unconditionalOfferStatus,
                    unconditionalOfferStatusId: university.unconditionalOfferStatusId,
                    dateOfUnconditionalOfferReceipt: university.dateOfUnconditionalOfferReceipt,
                    uploadedUnconditionalOfferLetter: university.uploadedUnconditionalOfferLetter,
                    totalFeeAmount: university.totalFeeAmount,
                    firstYearFeeAmount: university.firstYearFeeAmount,
                    feePaymentStatusId: university.feePaymentStatusId,
                    superAgents: university.superAgentId,
                    finalRemarks: university.finalRemarks,
                    updatedAt: new Date()
                });

                const universityRef = doc(db, `companies/${companyId}/leads/${leadId}/appliedUniversities`, university.universityId);
                batch.set(universityRef, sanitizedUniversity);
            });

            await batch.commit();

            // Update the lead document with the highest priority university's data
            const highestPriorityUniversity = selectedUniversities.find(u => u.priority === 1);
            if (highestPriorityUniversity && leadId) {
                // Sanitize the lead update data
                const sanitizedLeadUpdate = sanitizeData({
                    appliedUniversityId: highestPriorityUniversity.universityId,
                    countryId: highestPriorityUniversity.countryId,
                    countryName: highestPriorityUniversity.countryName,
                    admissionStatusId: highestPriorityUniversity.admissionStatusId,
                    subAdmissionStatusId: highestPriorityUniversity.subAdmissionStatusId || null,
                    agencyName: highestPriorityUniversity.agencyName,
                    programName: highestPriorityUniversity.programName,
                    programRemarks: highestPriorityUniversity.programRemarks,
                    applicationMovedStatusId: highestPriorityUniversity.applicationMovedStatusId,
                    dateOfApplicationSubmission: highestPriorityUniversity.dateOfApplicationSubmission,
                    conditionalOfferLetterStatusId: highestPriorityUniversity.conditionalOfferStatusId,
                    dateOfConditionalOfferReceipt: highestPriorityUniversity.dateOfConditionalOfferReceipt,
                    dateOfUnconditionalOfferReceipt: highestPriorityUniversity.dateOfUnconditionalOfferReceipt,
                    feePaymentStatusId: highestPriorityUniversity.feePaymentStatusId,
                    finalRemarks: highestPriorityUniversity.finalRemarks,
                    firstYearFeeAmount: highestPriorityUniversity.firstYearFeeAmount,
                    totalFeeAmount: highestPriorityUniversity.totalFeeAmount,
                    unconditionalOfferLetterStatusId: highestPriorityUniversity.unconditionalOfferStatusId,
                    uploadedAdmissionStatusDocument: highestPriorityUniversity.uploadedAdmissionStatusDocument,
                    uploadedConditionalOfferLetter: highestPriorityUniversity.uploadedConditionalOfferLetter,
                    uploadedUnconditionalOfferLetter: highestPriorityUniversity.uploadedUnconditionalOfferLetter,
                    superAgents: highestPriorityUniversity.superAgentId,
                    updatedAt: new Date()
                });

                const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
                await updateDoc(leadRef, sanitizedLeadUpdate);
            }

            // Update the lead document with the updatedAt and updatedBy fields
            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await updateDoc(leadRef, {
                updatedAt: serverTimestamp(),
                updatedBy: auth.currentUser?.uid,
            });

            setToastMessage('Universities and lead data saved successfully');
            setIsErrorToast(false);
            setShowToast(true);
            // Refresh the pipeline view if needed
            dispatch(setPipelineRefresh(true));
        } catch (error) {
            console.error('Error saving universities:', error);
            setToastMessage('Error saving universities');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsConfirmModalOpen(false);
            setAdditionalActions(true);
        }
    };

    // Handle file upload
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, universityId: string, type: 'admissionStatus' | 'conditional' | 'unconditional', priority: number) => {
        const file = event.target.files?.[0];
        if (!file || !leadId || !companyId) return;

        // Check file type
        if (!['image/jpeg', 'image/jpg', 'application/pdf'].includes(file.type)) {
            setToastMessage('Please upload only JPG, JPEG or PDF files');
            setIsErrorToast(true);
            setShowToast(true);
            return;
        }

        try {
            setIsUploading(true);
            const fileName = `${Date.now()}_${file.name}`;
            const storageRef = ref(storage, `student_documents/${companyId}/${leadId}/universities/${universityId}/${fileName}`);

            // Upload file
            await uploadBytes(storageRef, file);

            // Update both appliedUniversities subcollection and main lead document
            const batch = writeBatch(db);

            // Update appliedUniversities subcollection
            const universityRef = doc(db, `companies/${companyId}/leads/${leadId}/appliedUniversities`, universityId);
            batch.update(universityRef, {
                [type === 'admissionStatus' ? 'uploadedAdmissionStatusDocument' : type === 'conditional' ? 'uploadedConditionalOfferLetter' : 'uploadedUnconditionalOfferLetter']: fileName
            });

            if (priority === 1) {
                // Update main lead document
                const leadRef = doc(db, `companies/${companyId}/leads`, leadId);
                batch.update(leadRef, {
                    [type === 'admissionStatus' ? 'uploadedAdmissionStatusDocument' : type === 'conditional' ? 'uploadedConditionalOfferLetter' : 'uploadedUnconditionalOfferLetter']: fileName,
                    updatedAt: serverTimestamp(),
                    updatedBy: auth.currentUser?.uid,
                });
            }

            // Commit both updates
            await batch.commit();

            // Update local state
            const updatedUniversities = selectedUniversities.map(uni => {
                if (uni.universityId === universityId) {
                    return {
                        ...uni,
                        [type === 'admissionStatus' ? 'uploadedAdmissionStatusDocument' : type === 'conditional' ? 'uploadedConditionalOfferLetter' : 'uploadedUnconditionalOfferLetter']: fileName
                    };
                }
                return uni;
            });
            setSelectedUniversities(updatedUniversities);

            setToastMessage('File uploaded successfully');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error('Error uploading file:', error);
            setToastMessage('Error uploading file');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsUploading(false);
        }
    };

    // Handle file delete
    const handleFileDelete = async (universityId: string, type: 'admissionStatus' | 'conditional' | 'unconditional') => {
        const university = selectedUniversities.find(uni => uni.universityId === universityId);
        const fileName = type === 'admissionStatus' ? university?.uploadedAdmissionStatusDocument : type === 'conditional' ? university?.uploadedConditionalOfferLetter : university?.uploadedUnconditionalOfferLetter;
        if (!fileName || !leadId || !companyId) return;

        try {
            setIsUploading(true);

            // Delete from storage
            const storageRef = ref(storage, `student_documents/${companyId}/${leadId}/universities/${universityId}/${fileName}`);
            await deleteObject(storageRef);

            // Update both appliedUniversities subcollection and main lead document
            const batch = writeBatch(db);

            // Update appliedUniversities subcollection
            const universityRef = doc(db, `companies/${companyId}/leads/${leadId}/appliedUniversities`, universityId);
            batch.update(universityRef, {
                [type === 'admissionStatus' ? 'uploadedAdmissionStatusDocument' : type === 'conditional' ? 'uploadedConditionalOfferLetter' : 'uploadedUnconditionalOfferLetter']: ''
            });

            // Update main lead document
            const leadRef = doc(db, `companies/${companyId}/leads`, leadId);
            batch.update(leadRef, {
                [type === 'admissionStatus' ? 'uploadedAdmissionStatusDocument' : type === 'conditional' ? 'uploadedConditionalOfferLetter' : 'uploadedUnconditionalOfferLetter']: '',
                updatedAt: serverTimestamp(),
                updatedBy: auth.currentUser?.uid,
            });

            // Commit both updates
            await batch.commit();

            // Update local state
            const updatedUniversities = selectedUniversities.map(uni => {
                if (uni.universityId === universityId) {
                    const updatedUni = { ...uni };
                    if (type === 'admissionStatus') {
                        updatedUni.uploadedAdmissionStatusDocument = '';
                    } else if (type === 'conditional') {
                        updatedUni.uploadedConditionalOfferLetter = '';
                    } else {
                        updatedUni.uploadedUnconditionalOfferLetter = '';
                    }
                    return updatedUni;
                }
                return uni;
            });
            setSelectedUniversities(updatedUniversities);

            setToastMessage('File deleted successfully');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error('Error deleting file:', error);
            setToastMessage('Error deleting file');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsUploading(false);
        }
    };

    // Handle file download
    const handleFileDownload = async (universityId: string, type: 'admissionStatus' | 'conditional' | 'unconditional') => {
        const university = selectedUniversities.find(uni => uni.universityId === universityId);
        const fileName = type === 'admissionStatus' ? university?.uploadedAdmissionStatusDocument : type === 'conditional' ? university?.uploadedConditionalOfferLetter : university?.uploadedUnconditionalOfferLetter;
        if (!fileName || !leadId || !companyId) return;

        try {
            const storageRef = ref(storage, `student_documents/${companyId}/${leadId}/universities/${universityId}/${fileName}`);
            const url = await getDownloadURL(storageRef);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error downloading file:', error);
            setToastMessage('Error downloading file');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };

    const toggleCollapse = (universityId: string) => {
        setCollapsedUniversities(prev => {
            const newSet = new Set(Array.from(prev));
            if (newSet.has(universityId)) {
                newSet.delete(universityId);
            } else {
                newSet.add(universityId);
            }
            return newSet;
        });
    };

    const inputClass = `w-full py-2.5 px-4 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 shadow-sm hover:border-cyan-300 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`;
    const labelClass = "block font-montserrat text-sm font-medium text-gray-700 mb-1.5";
    const sectionClass = "mb-8 bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300";
    const sectionHeaderClass = "text-xl font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200";

    return (
        <div className="mt-8 mx-10 space-y-6">
            {/* Save Button */}
            {write && (
                <div className="flex justify-end">
                    <button
                        onClick={confirmSave}
                        className="px-6 py-2.5 bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white rounded-full hover:from-cyan-600 hover:to-cyan-700 transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span>Save Changes</span>
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* University Selection Section */}
            <div className={sectionClass}>
                {write && (
                    <div className="flex justify-between items-center mb-4">
                        <h2 className={sectionHeaderClass}>Applied Universities</h2>
                        <button
                            onClick={() => setIsUniversityDropdownOpen(true)}
                            className="px-4 py-2.5 bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white rounded-full transition-all duration-200 flex items-center gap-2 shadow-sm hover:shadow-md"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Add University
                        </button>
                    </div>
                )}

                {/* University Search Input */}
                {isUniversityDropdownOpen && (
                    <div className="relative mb-4">
                        <input
                            ref={universityInputRef}
                            type="text"
                            value={universitySearchTerm}
                            onChange={handleUniversitySearchChange}
                            placeholder="Type 3 or more characters to search universities"
                            className={inputClass}
                        />
                        {/* University Search Results */}
                        {universitySearchTerm.length >= 3 && (
                            <div
                                ref={universityDropdownRef}
                                className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto backdrop-blur-sm backdrop-filter"
                            >
                                {isLoadingUniversities ? (
                                    <div className="p-2 text-center text-gray-500">Loading...</div>
                                ) : universities.length > 0 ? (
                                    universities.map((university) => (
                                        <div
                                            key={university.id}
                                            onClick={() => handleUniversitySelect(university)}
                                            className="p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                                        >
                                            {university.name}
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-2 text-center text-gray-500">No universities found</div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Selected Universities List */}
                <div className="space-y-4">
                    {selectedUniversities.map((university) => (
                        <div
                            key={university.universityId}
                            className={`group border border-gray-200 rounded-xl bg-white shadow-sm transition-all duration-300 ${!collapsedUniversities.has(university.universityId) ? 'ring-1 ring-cyan-500' : 'hover:border-cyan-200 hover:shadow-md'}`}
                        >
                            <div
                                className="p-5 cursor-pointer"
                                onClick={() => toggleCollapse(university.universityId)}
                            >
                                {/* University header with toggle button */}
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-cyan-700 transition-colors">
                                            {university.universityName}
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveUniversity(university.universityId, 'up');
                                                }}
                                                className="p-1 text-gray-500 hover:text-cyan-600 disabled:opacity-50"
                                                disabled={university.priority === 1}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveUniversity(university.universityId, 'down');
                                                }}
                                                className="p-1 text-gray-500 hover:text-cyan-600 disabled:opacity-50"
                                                disabled={university.priority === selectedUniversities.length}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                            <span className="text-sm text-gray-500">Priority: {university.priority}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-cyan-600 group-hover:text-cyan-700 transition-colors">
                                        {university.programName && (
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-semibold text-gray-900 group-hover:text-cyan-700 transition-colors">
                                                    Program Name: {university.programName}
                                                </h3>
                                            </div>
                                        )}
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={`h-6 w-6 transform transition-transform duration-200 ${collapsedUniversities.has(university.universityId) ? '' : 'rotate-180'}`}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            {!collapsedUniversities.has(university.universityId) && (
                                <div className="p-5 pt-0">
                                    {/* Application Status Section */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {/* Left Column */}
                                        <div className="space-y-6">
                                            {/* Application Status Section */}
                                            <div className="p-5 bg-gray-50/70 rounded-xl border border-gray-100 backdrop-blur-sm">
                                                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">Basic Information</h4>
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <label className={labelClass}>Admission Status</label>
                                                        <select
                                                            value={university.admissionStatusId}
                                                            onChange={(e) => {
                                                                const selectedStatus = admissionStatuses.find(status => status.id === e.target.value);
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return {
                                                                            ...uni,
                                                                            admissionStatusId: e.target.value,
                                                                            admissionStatus: selectedStatus?.admissionStatus || ''
                                                                        };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            disabled={!write}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {admissionStatuses.map(status => (
                                                                <option key={status.id} value={status.id}>{status.admissionStatus}</option>
                                                            ))}
                                                        </select>
                                                        {university.admissionStatusId && leadAdmissionSubStatuses.length > 0 && leadAdmissionSubStatuses.some(status => status.admissionStatusId === university.admissionStatusId) && (
                                                            <>
                                                                <label className={labelClass}>Admission Sub Status</label>
                                                                <select
                                                                    value={university.subAdmissionStatusId}
                                                                    onChange={(e) => {
                                                                        const updatedUniversities = selectedUniversities.map(uni => {
                                                                            if (uni.universityId === university.universityId) {
                                                                                return {
                                                                                    ...uni,
                                                                                    subAdmissionStatusId: e.target.value
                                                                                };
                                                                            }
                                                                            return uni;
                                                                        });
                                                                        setSelectedUniversities(updatedUniversities);
                                                                    }}
                                                                    className={inputClass}
                                                                    disabled={!write}
                                                                >
                                                                    <option value="">Select Sub Status</option>
                                                                    {leadAdmissionSubStatuses
                                                                        .filter(status => status.admissionStatusId === university.admissionStatusId)
                                                                        .map(status => (
                                                                            <option key={status.id} value={status.id}>{status.subAdmissionStatus}</option>
                                                                        ))}
                                                                </select>
                                                            </>
                                                        )}
                                                        <div
                                                            onClick={() => {
                                                                if (university.uploadedAdmissionStatusDocument) {
                                                                    handleFileDownload(university.universityId, 'admissionStatus');
                                                                } else {
                                                                    document.getElementById(`admission-status-${university.universityId}`)?.click();
                                                                }
                                                            }}
                                                            className={`${!write ? 'cursor-not-allowed' : 'cursor-pointer'} border rounded-lg p-3 relative ${university.uploadedAdmissionStatusDocument ? 'hover:bg-cyan-50/50' : 'hover:bg-gray-50'}`}
                                                        >
                                                            <input
                                                                type="file"
                                                                accept=".jpg,.jpeg,.pdf"
                                                                onChange={(e) => handleFileUpload(e, university.universityId, 'admissionStatus', university.priority)}
                                                                className={`hidden ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                id={`admission-status-${university.universityId}`}
                                                                readOnly={!write}
                                                                disabled={!write}
                                                            />
                                                            <div className="flex items-center justify-between">
                                                                <span className={university.uploadedAdmissionStatusDocument ? 'text-cyan-600' : 'text-gray-400'}>
                                                                    {university.uploadedAdmissionStatusDocument || 'Upload Document (JPG, JPEG, PDF)'}
                                                                </span>
                                                                {university.uploadedAdmissionStatusDocument && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleFileDelete(university.universityId, 'admissionStatus');
                                                                        }}
                                                                        className={`text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                        disabled={!write}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {isUploading && (
                                                                <div className="absolute inset-0 bg-gray-100 bg-opacity-50 flex items-center justify-center">
                                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <label className={labelClass}>Country</label>
                                                        <div className="relative">
                                                            <input
                                                                type="text"
                                                                value={countrySearchTerm || university.countryName}
                                                                onChange={(e) => {
                                                                    if (!e.target.value) {
                                                                        // Clear the country when input is empty
                                                                        const updatedUniversities = selectedUniversities.map(uni => {
                                                                            if (uni.universityId === university.universityId) {
                                                                                return { ...uni, countryId: '', countryName: '' };
                                                                            }
                                                                            return uni;
                                                                        });
                                                                        setSelectedUniversities(updatedUniversities);
                                                                    }
                                                                    handleCountrySearchChange(university.universityId, e);
                                                                }}
                                                                onFocus={() => {
                                                                    setCountrySearchTerm(university.countryName);
                                                                    setIsCountryDropdownOpen({ ...isCountryDropdownOpen, [university.universityId]: true });
                                                                }}
                                                                placeholder="Type 3 or more characters to search countries"
                                                                className={inputClass}
                                                                readOnly={!write}
                                                                disabled={!write}
                                                            />
                                                            {isCountryDropdownOpen[university.universityId] && (countrySearchTerm.length >= 3 || countries.length > 0) && (
                                                                <div className="absolute left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                                                                    {isLoadingCountry ? (
                                                                        <div className="p-2 text-center text-gray-500">Loading...</div>
                                                                    ) : countries.length > 0 ? (
                                                                        countries.map((country) => (
                                                                            <button
                                                                                key={country.id}
                                                                                onClick={() => handleCountrySelect(university.universityId, country)}
                                                                                className="w-full py-2 px-4 text-left hover:bg-gray-100"
                                                                            >
                                                                                {country.name}
                                                                            </button>
                                                                        ))
                                                                    ) : (
                                                                        countrySearchTerm.length >= 3 && (
                                                                            <div className="p-2 text-center text-gray-500">No countries found</div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <label className={labelClass}>Agent/Agency Name</label>
                                                        <input
                                                            type="text"
                                                            value={university.agencyName}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, agencyName: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            placeholder="Enter Agency Name"
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />

                                                        <label className={labelClass}>Program Name</label>
                                                        <input
                                                            type="text"
                                                            value={university.programName}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, programName: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            placeholder="Enter Program Name"
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />

                                                        <div className='relative'>
                                                            <label className={labelClass}>Course Link</label>
                                                            <input
                                                                type="text"
                                                                value={university.courseLink}
                                                                onChange={(e) => {
                                                                    const updatedUniversities = selectedUniversities.map(uni => {
                                                                        if (uni.universityId === university.universityId) {
                                                                            return { ...uni, courseLink: e.target.value };
                                                                        }
                                                                        return uni;
                                                                    });
                                                                    setSelectedUniversities(updatedUniversities);
                                                                }}
                                                                placeholder="Enter Course Link"
                                                                className={inputClass}
                                                                readOnly={!write}
                                                                disabled={!write}
                                                            />
                                                            <button
                                                                onClick={() => {
                                                                    if (university.courseLink) {
                                                                        window.open(university.courseLink, '_blank');
                                                                    }
                                                                }}
                                                                className={`absolute right-2 top-1/2 transform -translate-y-1/5 text-gray-500 hover:text-cyan-600 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                disabled={!write}
                                                            >
                                                                <img src={`${process.env.PUBLIC_URL}/images/desktop/newWindow.svg`} alt="Link Icon" className="h-5 w-5" />
                                                            </button>
                                                        </div>

                                                        <label className={labelClass}>Program Remarks (if any)</label>
                                                        <textarea
                                                            value={university.programRemarks}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, programRemarks: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            placeholder="Enter program remarks"
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-5 bg-gray-50/70 rounded-xl border border-gray-100 backdrop-blur-sm mt-6">
                                                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">Application Status</h4>
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <label className={labelClass}>Application Moved Status</label>
                                                        <select
                                                            value={university.applicationMovedStatusId}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, applicationMovedStatusId: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            disabled={!write}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {applicationMovedStatuses.map(status => (
                                                                <option key={status.id} value={status.id}>{status.applicationMovedStatus}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className={labelClass}>Date of Application Submission</label>
                                                        <input
                                                            type="date"
                                                            value={university.dateOfApplicationSubmission ? university.dateOfApplicationSubmission.toDate().toISOString().split('T')[0] : ''}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, dateOfApplicationSubmission: e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : null };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Conditional Offer Details Section */}
                                            <div className="p-5 bg-gray-50/70 rounded-xl border border-gray-100 backdrop-blur-sm">
                                                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">Conditional Offer Details</h4>
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <label className={labelClass}>Conditional Offer Status</label>
                                                        <select
                                                            value={university.conditionalOfferStatusId}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, conditionalOfferStatusId: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            disabled={!write}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {offerLetterStatuses.map(status => (
                                                                <option key={status.id} value={status.id}>{status.offerLetterStatus}</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="date"
                                                            value={university.dateOfConditionalOfferReceipt ? university.dateOfConditionalOfferReceipt.toDate().toISOString().split('T')[0] : ''}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, dateOfConditionalOfferReceipt: e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : null };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />
                                                        <div
                                                            onClick={() => {
                                                                if (university.uploadedConditionalOfferLetter) {
                                                                    handleFileDownload(university.universityId, 'conditional');
                                                                } else {
                                                                    document.getElementById(`conditional-offer-${university.universityId}`)?.click();
                                                                }
                                                            }}
                                                            className={`cursor-pointer border rounded-lg p-3 relative ${university.uploadedConditionalOfferLetter ? 'hover:bg-cyan-50/50' : 'hover:bg-gray-50'}`}
                                                        >
                                                            <input
                                                                type="file"
                                                                accept=".jpg,.jpeg,.pdf"
                                                                onChange={(e) => handleFileUpload(e, university.universityId, 'conditional', university.priority)}
                                                                className={`hidden ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                readOnly={!write}
                                                                disabled={!write}
                                                                id={`conditional-offer-${university.universityId}`}
                                                            />
                                                            <div className="flex items-center justify-between">
                                                                <span className={university.uploadedConditionalOfferLetter ? 'text-cyan-600' : 'text-gray-400'}>
                                                                    {university.uploadedConditionalOfferLetter || 'Upload Offer Letter (JPG, JPEG, PDF)'}
                                                                </span>
                                                                {university.uploadedConditionalOfferLetter && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleFileDelete(university.universityId, 'conditional');
                                                                        }}
                                                                        className={`text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                        disabled={!write}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {isUploading && (
                                                                <div className="absolute inset-0 bg-gray-100 bg-opacity-50 flex items-center justify-center">
                                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Column */}
                                        <div className="space-y-6">
                                            {/* Unconditional Offer Details Section */}
                                            <div className="p-5 bg-gray-50/70 rounded-xl border border-gray-100 backdrop-blur-sm">
                                                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">Unconditional Offer Details</h4>
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <label className={labelClass}>Unconditional Offer Status</label>
                                                        <select
                                                            value={university.unconditionalOfferStatusId}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, unconditionalOfferStatusId: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            disabled={!write}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {offerLetterStatuses.map(status => (
                                                                <option key={status.id} value={status.id}>{status.offerLetterStatus}</option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            type="date"
                                                            value={university.dateOfUnconditionalOfferReceipt ? university.dateOfUnconditionalOfferReceipt.toDate().toISOString().split('T')[0] : ''}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, dateOfUnconditionalOfferReceipt: e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : null };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />
                                                        <div
                                                            onClick={() => {
                                                                if (university.uploadedUnconditionalOfferLetter) {
                                                                    handleFileDownload(university.universityId, 'unconditional');
                                                                } else {
                                                                    document.getElementById(`unconditional-offer-${university.universityId}`)?.click();
                                                                }
                                                            }}
                                                            className={`cursor-pointer border rounded-lg p-3 relative ${university.uploadedUnconditionalOfferLetter ? 'hover:bg-cyan-50/50' : 'hover:bg-gray-50'}`}
                                                        >
                                                            <input
                                                                type="file"
                                                                accept=".jpg,.jpeg,.pdf"
                                                                onChange={(e) => handleFileUpload(e, university.universityId, 'unconditional', university.priority)}
                                                                className={`hidden ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                readOnly={!write}
                                                                disabled={!write}
                                                                id={`unconditional-offer-${university.universityId}`}
                                                            />
                                                            <div className="flex items-center justify-between">
                                                                <span className={university.uploadedUnconditionalOfferLetter ? 'text-cyan-600' : 'text-gray-400'}>
                                                                    {university.uploadedUnconditionalOfferLetter || 'Upload Offer Letter (JPG, JPEG, PDF)'}
                                                                </span>
                                                                {university.uploadedUnconditionalOfferLetter && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleFileDelete(university.universityId, 'unconditional');
                                                                        }}
                                                                        className={`text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                                        disabled={!write}
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {isUploading && (
                                                                <div className="absolute inset-0 bg-gray-100 bg-opacity-50 flex items-center justify-center">
                                                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Fee Payment Details Section */}
                                            <div className="p-5 bg-gray-50/70 rounded-xl border border-gray-100 backdrop-blur-sm">
                                                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">Fee Payment Details (in ₹)</h4>
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <label className={labelClass}>Total Fee Amount</label>
                                                        <input
                                                            type="text"
                                                            value={university.totalFeeAmount}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, totalFeeAmount: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />
                                                        <label className={labelClass}>First Year Fee Amount</label>
                                                        <input
                                                            type="text"
                                                            value={university.firstYearFeeAmount}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, firstYearFeeAmount: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            readOnly={!write}
                                                            disabled={!write}
                                                        />
                                                        <label className={labelClass}>Fee Payment Status</label>
                                                        <select
                                                            value={university.feePaymentStatusId}
                                                            onChange={(e) => {
                                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, feePaymentStatusId: e.target.value };
                                                                    }
                                                                    return uni;
                                                                });
                                                                setSelectedUniversities(updatedUniversities);
                                                            }}
                                                            className={inputClass}
                                                            disabled={!write}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {feePaymentStatuses.map(status => (
                                                                <option key={status.id} value={status.id}>{status.feePaymentStatus}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Super Agents Section */}
                                            <div className="p-5 bg-gray-50/70 rounded-xl border border-gray-100 backdrop-blur-sm">
                                                <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">Super Agents</h4>
                                                <div className="space-y-6">
                                                    <div className="space-y-2">
                                                        <label className={labelClass}>Super Agent Name</label>
                                                        <select
                                                            onChange={(e) =>
                                                                setSelectedUniversities(selectedUniversities.map(uni => {
                                                                    if (uni.universityId === university.universityId) {
                                                                        return { ...uni, superAgentId: e.target.value };
                                                                    }
                                                                    return uni;
                                                                }))
                                                            }
                                                            value={university.superAgentId || ''}
                                                            className={inputClass}
                                                            disabled={!write}
                                                        >
                                                            <option value="">Select Super Agent</option>
                                                            {superAgents.map(agent => (
                                                                <option key={agent.id} value={agent.id}>{agent.superAgent}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Final Remarks Section */}
                                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                                        <h4 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">Final Remarks of Application Status</h4>
                                        <textarea
                                            value={university.finalRemarks}
                                            onChange={(e) => {
                                                const updatedUniversities = selectedUniversities.map(uni => {
                                                    if (uni.universityId === university.universityId) {
                                                        return { ...uni, finalRemarks: e.target.value };
                                                    }
                                                    return uni;
                                                });
                                                setSelectedUniversities(updatedUniversities);
                                            }}
                                            className={inputClass + " w-full h-24"}
                                            readOnly={!write}
                                            disabled={!write}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <ConfirmationModal isOpen={isConfirmModalOpen} isAdditional={isAdditionalActions} onClose={() => setIsConfirmModalOpen(false)} onSave={handleSave}
                selectedStudent={{ id: leadId, name: leadName, phone: leadPhone }} />

            {/* Toast Notification */}
            <Toast
                message={toastMessage}
                isError={isErrorToast}
                onClose={() => setShowToast(false)}
                show={showToast}
            />
        </div>
    );
};

export default AppliedUniversities;